import React, { useEffect, useState } from "react";
import { Mail, Inbox, Send, Bot, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";

// Extract the bare email from an RFC822-style "Name <a@b.c>" or just "a@b.c".
function bareEmail(s) {
  if (!s) return "";
  const m = String(s).match(/<([^>]+)>/);
  return (m ? m[1] : s).trim();
}

function domainOf(s) {
  const e = bareEmail(s);
  const i = e.lastIndexOf("@");
  return i === -1 ? "" : e.slice(i + 1);
}

export default function AdminEmailSettings() {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/email-settings")
      .then(({ data }) => setCfg(data))
      .catch(() => setErr("Could not load email settings"));
  }, []);

  const inbox = bareEmail(cfg?.inbox);
  const manual = bareEmail(cfg?.manual_sender);
  const system = bareEmail(cfg?.system_sender);
  const domain = domainOf(cfg?.inbox) || "icefleet.is";

  return (
    <div className="space-y-8" data-testid="admin-email-settings">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Email settings</h1>
        <p className="mt-1 text-sm text-slate-600">
          Three addresses. Clear roles. Everything lands in <strong>{inbox || "info@icefleet.is"}</strong>.
        </p>
      </div>

      {err && <div className="rounded-md bg-rose-50 p-3 text-sm text-rose-800">{err}</div>}

      {cfg && !cfg.configured && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Resend API key not configured</p>
            <p className="mt-0.5">
              Emails won't be delivered until you add <code className="rounded bg-white px-1.5 py-0.5">RESEND_API_KEY</code> in <code className="rounded bg-white px-1.5 py-0.5">/app/backend/.env</code> and verify the <code>{domain}</code> domain in your Resend dashboard.
            </p>
          </div>
        </div>
      )}

      {cfg?.configured && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">
          <CheckCircle2 size={16} /> Resend is configured and sending is live.
        </div>
      )}

      {/* 3 addresses */}
      <section className="grid gap-4 md:grid-cols-3">
        <AddressCard
          icon={<Inbox size={16} />}
          color="sky"
          name={inbox || `info@${domain}`}
          role="Main inbox"
          note="All website forms, availability requests, and general inquiries arrive here."
        />
        <AddressCard
          icon={<Send size={16} />}
          color="indigo"
          name={manual || `bookings@${domain}`}
          role="Manual customer replies"
          note="Every admin reply is sent FROM this address. Availability offers, booking confirmations, follow-ups."
        />
        <AddressCard
          icon={<Bot size={16} />}
          color="slate"
          name={system || `noreply@${domain}`}
          role="Automated system"
          note="Instant 'request received' auto-replies. Replies route back to bookings@ via Reply-To header."
        />
      </section>

      {/* Routing table */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Where each email goes</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="py-2 pr-4">Event</th>
                <th className="py-2 pr-4">Delivered to</th>
                <th className="py-2">Auto-reply from</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(cfg?.routing || []).map((r, i) => (
                <tr key={i}>
                  <td className="py-3 pr-4 font-medium text-slate-800">{r.event}</td>
                  <td className="py-3 pr-4 text-slate-700">{r.delivered_to}</td>
                  <td className="py-3 text-slate-700">{r.auto_reply_from}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* How to use the templates */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="font-heading text-lg font-semibold text-slate-900">How to switch sender & use templates</h2>
        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-slate-700">
          <li>Go to <strong>Admin → Bookings</strong>.</li>
          <li>Click <strong>Email</strong> on any booking row.</li>
          <li>
            Use the <strong>Template</strong> dropdown to pick <em>Availability reply</em> or <em>Booking confirmation</em>. The email is always sent FROM <code>{manual}</code>.
          </li>
          <li>Edit the subject or message freely, then click <strong>Send email</strong>.</li>
        </ol>
        <p className="mt-3 text-xs text-slate-500">
          Automated system emails (sent FROM <code>{system}</code>) fire automatically when a visitor submits the website contact or availability form — no action needed from admin.
        </p>
      </section>

      {/* Layer 1 — mailbox-provider setup */}
      <section className="rounded-xl border border-slate-200 bg-slate-50 p-5">
        <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Mail size={18} /> Microsoft 365 setup (one-time)
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Do this once in your Microsoft 365 admin center — it configures the <em>receiving</em> side that this app can't control.
        </p>
        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-slate-700">
          <li>Create the three mailboxes: <code>info@{domain}</code>, <code>bookings@{domain}</code>, <code>noreply@{domain}</code>.</li>
          <li>
            In <strong>Exchange admin center → Mailboxes → bookings@{domain} → Mail flow settings → Forwarding</strong>: forward to <code>info@{domain}</code> and tick "Keep a copy".
          </li>
          <li>
            For <strong>noreply@{domain}</strong>: set up a mail-flow rule to <em>reject</em> incoming replies with the message "This mailbox is not monitored. Please contact info@{domain}".
          </li>
          <li>
            Set <strong>Automatic replies</strong> on <code>info@{domain}</code> (Outlook → File → Automatic Replies) using the "We received your request – IceFleet" copy.
          </li>
          <li>
            Set a second auto-reply on <code>bookings@{domain}</code> using the "Booking request received – IceFleet" copy.
          </li>
          <li>
            In Outlook, add <code>bookings@{domain}</code> and <code>noreply@{domain}</code> as <strong>"Send As"</strong> aliases on your primary account so you can switch sender from the compose window.
          </li>
          <li>
            Verify <strong>{domain}</strong> in your Resend dashboard and paste the Resend API key into <code>/app/backend/.env</code> → <code>RESEND_API_KEY</code>.
          </li>
        </ol>
      </section>
    </div>
  );
}

const COLOR_MAP = {
  sky: "bg-sky-50 text-sky-900 border-sky-200",
  indigo: "bg-indigo-50 text-indigo-900 border-indigo-200",
  slate: "bg-slate-50 text-slate-900 border-slate-200",
};

function AddressCard({ icon, color, name, role, note }) {
  return (
    <div className={`rounded-xl border p-4 ${COLOR_MAP[color] || COLOR_MAP.slate}`}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-80">
        {icon} {role}
      </div>
      <div className="mt-2 font-mono text-sm font-semibold">{name}</div>
      <p className="mt-2 text-xs leading-relaxed opacity-80">{note}</p>
    </div>
  );
}
